const isDev = process.env.NODE_ENV !== 'production';
let hasLoggedEnvCheck = false;
let nativeConfig: Record<string, string | undefined> = {};
let platformSuffix = '';

try {
	const configModule = require('react-native-config');
	nativeConfig = (configModule?.default ?? configModule ?? {}) as Record<
		string,
		string | undefined
	>;
} catch (error) {
	if (isDev) {
		const message = error instanceof Error ? error.message : String(error);
		console.warn('[ENV] Failed to load react-native-config:', message);
	}
}

try {
	const platformModule = require('react-native');
	const os = platformModule?.Platform?.OS;
	platformSuffix = os ? `_${String(os).toUpperCase()}` : '';
} catch {
	platformSuffix = '';
}

const requiredEnvKeys = [
	'CASINO_ID',
	'SVARA_API_URL',
	'PLATFORM_API_URL',
	'FACEBOOK_APP_ID',
	'WS_API_URL',
	'SVARA_WEB_URL',
	'UNITY_ADS_APP_KEY',
	'UNITY_ADS_AD_UNIT_ID',
];

const getFromSources = (key: string): string | undefined => {
	if (typeof process !== 'undefined' && process.env && key in process.env) return process.env[key];
	if (nativeConfig && key in nativeConfig) return nativeConfig[key];
	if (typeof window !== 'undefined' && (window as any).__ENV__?.[key])
		return (window as any).__ENV__[key];
	return undefined;
};

const resolveEnvValue = (key: string, fallback?: string): string | undefined => {
	const platformKeys = platformSuffix ? [`${key}${platformSuffix}`] : [];

	// Support APPLE suffix for iOS-specific environment variables.
	if (platformSuffix === '_IOS') {
		platformKeys.push(`${key}_APPLE`);
	}

	for (const platformKey of platformKeys) {
		const platformValue = getFromSources(platformKey);
		if (platformValue) return platformValue;
	}

	const defaultValue = getFromSources(key);
	if (defaultValue) return defaultValue;

	return fallback;
};

const logEnvLoadedCheck = () => {
	if (!isDev || hasLoggedEnvCheck) return;
	hasLoggedEnvCheck = true;

	const envCheck = requiredEnvKeys.reduce<Record<string, boolean>>((acc, key) => {
		acc[key] = !!resolveEnvValue(key);
		return acc;
	}, {});

	envCheck.DEV_MODE = !!resolveEnvValue('DEV_MODE');

	console.log('🧩 [ENV] Loaded check:', envCheck);

	const missingRequired = requiredEnvKeys.filter(key => !resolveEnvValue(key));
	if (missingRequired.length > 0) {
		throw new Error(`[ENV] Missing required env vars: ${missingRequired.join(', ')}`);
	}
};

export function env(key: string, fallback?: string): string | undefined {
	logEnvLoadedCheck();
	const value = resolveEnvValue(key, fallback);
	if (!value && !fallback) {
		throw new Error(`[ENV] Missing env value for key: ${key}`);
	}
	return value;
}

/**
 * A genuinely optional variable: undefined when absent or empty, never throws.
 * `env(key, '')` cannot express this — an empty fallback still counts as missing.
 */
export function optionalEnv(key: string): string | undefined {
	logEnvLoadedCheck();
	return resolveEnvValue(key) || undefined;
}
